import type { RecipeLimit, RecipeStatus, RecipeView } from '../api/missions';
import { ca } from '../i18n/ca';
import { meansOf } from './startMeans';

/**
 * What a recipe lets the wizard offer (docs/design/ENGINE_DESIGN.md §2.18–§2.20,
 * MECHANICS.md R17), read from `/api/recipes`. Two rules, the owner's of
 * 2026-09-27: a whole recipe this server, map or faction cannot play is shown
 * greyed with the reason; a parameter or a value the chosen recipe does not
 * admit is hidden, not greyed, except the supports (the owner, 2026-10-13): the
 * last step lists every support of the catalogue and padlocks the ones the type
 * does not admit, with the reason. A recipe list from before the constraints
 * existed answers what the wizard always offered.
 */

/** The hours the type step has always offered, and the night ones of them (Recipe.isNightHour). */
export const ALL_HOURS = [4.5, 6, 8, 12, 17.5, 20, 22.5, 1];
export const NIGHT_HOURS = ALL_HOURS.filter((h) => h >= 20 || h <= 4.5);

function inWindow(h: number, from: number, to: number): boolean {
  return from <= to ? h >= from && h <= to : h >= from || h <= to;
}

/**
 * The hours a recipe may start at. A window of its own narrows the usual
 * list; one too narrow for it (night_raid's 01:30–05:00) is offered by the
 * half hour instead, so there is always more than a single choice.
 */
export function hourOptions(recipe: RecipeView | null): number[] {
  const w = recipe?.constraints?.hours;
  if (w) {
    const usual = ALL_HOURS.filter((h) => inWindow(h, w.from, w.to));
    if (usual.length >= 3) return usual;
    const grid: number[] = [];
    for (let h = 0; h < 24; h += 0.5) if (inWindow(h, w.from, w.to)) grid.push(h);
    return grid;
  }
  return recipe?.nightOnly ? NIGHT_HOURS : ALL_HOURS;
}

/** The sentence under the hour field, when the recipe decides when it is played. */
export function hourHint(recipe: RecipeView | null): string | null {
  return (
    recipe?.constraints?.hours?.reason ?? (recipe?.nightOnly ? ca.missionUi.nightOnlyHint : null)
  );
}

/** The weathers a recipe admits, in the order the wizard names them. */
export function weatherOptions(recipe: RecipeView | null): string[] {
  const all = Object.keys(ca.weatherNames);
  const admitted = recipe?.constraints?.weather;
  return admitted ? all.filter((w) => admitted.includes(w)) : all;
}

/**
 * One control of the last step as the recipe leaves it (RecipeLimits on the
 * platform): free, limited or fixed, with the range in words and the reason.
 * Null from a platform older than the field, and then the control is drawn as
 * it always was, inside what the constraints admit.
 */
export function limitOf(
  recipe: RecipeView | null,
  param: RecipeLimit['param'],
): RecipeLimit | null {
  return recipe?.limits?.find((l) => l.param === param) ?? null;
}

/** Whether the recipe decides it: shown with a lock, never edited, never voted. */
export function isFixed(recipe: RecipeView | null, param: RecipeLimit['param']): boolean {
  return limitOf(recipe, param)?.state === 'fixed';
}

/** How many may play: the recipe's declared range, else the range its card has always said. */
export function playersRange(recipe: RecipeView): { min: number; max: number } {
  return recipe.constraints?.players ?? { min: recipe.minPlayers, max: recipe.maxPlayers };
}

/** Whether a way in ("helicòpter", "a peu"…) is one the recipe admits. */
export function admitsIn(recipe: RecipeView | null, option: string): boolean {
  const list = recipe?.constraints?.insertion;
  return !list || list.includes(meansOf(option));
}

/** Whether a way out is one the recipe admits. */
export function admitsOut(recipe: RecipeView | null, option: string): boolean {
  const list = recipe?.constraints?.extraction;
  return !list || list.includes(meansOf(option));
}

/** Whether the night may begin there: land, the sea, or a point of the creator's. */
export function admitsStart(recipe: RecipeView | null, from: 'land' | 'sea' | 'point'): boolean {
  const list = recipe?.constraints?.startFrom;
  return !list || list.includes(from);
}

/** Whether a support may be asked for. */
export function admitsSupport(recipe: RecipeView | null, key: string): boolean {
  const list = recipe?.constraints?.supports;
  return !list || list.includes(key);
}

/** Lower case with the accents and the middle dot gone, so «incursio» finds «Incursió». */
function fold(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/·/g, '').toLowerCase();
}

/**
 * Whether a type answers a search on the type step: every word typed is somewhere
 * in its name or its short description, in any case and with or without accents.
 * An empty search finds every type.
 */
export function matchesRecipeSearch(recipe: RecipeView, query: string): boolean {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = fold(`${recipe.title} ${recipe.blurb ?? ''}`);
  return words.every((w) => haystack.includes(w));
}

/** A type the generator cannot build yet: listed apart, never picked. */
export function isPlanned(recipe: RecipeView): boolean {
  return recipe.planned ?? (!recipe.available && !recipe.unavailableReason);
}

/**
 * How far a type is from a night the group can rely on (the owner, 2026-09-27;
 * RecipeStatus on the platform): `ready` and `testing` may be picked, the
 * second marked «En proves»; `coming` is shown as «Properament» and never
 * picked. A list from before the platform said so answers what it always
 * offered: a planned type is coming, every other one ready.
 */
export function statusOf(recipe: RecipeView): RecipeStatus {
  return recipe.status ?? (isPlanned(recipe) ? 'coming' : 'ready');
}

/** A type nobody may pick yet: a planned one, or one the owner has not put in testing. */
export function isComing(recipe: RecipeView): boolean {
  return statusOf(recipe) === 'coming';
}

/**
 * A coming type an admin may still pick, as the platform lets an admin generate
 * it: one the generator builds, never a planned one it cannot.
 */
export function adminOnly(recipe: RecipeView, isAdmin: boolean): boolean {
  return isAdmin && isComing(recipe) && !isPlanned(recipe);
}

/** A type that is scripted and implemented but not accepted yet: picked, and marked. */
export function isTesting(recipe: RecipeView): boolean {
  return statusOf(recipe) === 'testing';
}

/**
 * What a type in tests says on its chip (the owner, 2026-10-12): «En proves · N de M» with the test
 * cards it has passed and the ones it needs; «En proves» alone when it needs none or the platform
 * says no count (an older one, or a list that never asked).
 */
export function testingLabel(recipe: RecipeView): string {
  const passed = recipe.testsPassed;
  const required = recipe.testsRequired;
  if (typeof passed !== 'number' || typeof required !== 'number' || required <= 0) {
    return ca.missionUi.testing;
  }
  return ca.missionUi.testingProgress(passed, required);
}

/** A type this server, map or faction cannot play: shown greyed, with the reason. */
export function isGreyed(recipe: RecipeView): boolean {
  return !isComing(recipe) && (recipe.compatible === false || !!recipe.unavailableReason);
}

/** Why a greyed type cannot be played, and what would make it fit. */
export function greyReason(recipe: RecipeView): { text: string; fix: string | null } | null {
  const first = recipe.reasons?.[0];
  if (first) return { text: first.reason, fix: ca.missionUi.fixBy[first.fixBy] ?? null };
  if (recipe.unavailableReason)
    return { text: ca.missionUi.needsGround(recipe.unavailableReason), fix: null };
  return null;
}
